// @flow
import React, { type Element, useState } from 'react';
import Loader from 'angieslist-app-platform/lib/browser/Loader';
import { get } from 'lodash';
import { analyticsActions, urlCatalog, Track } from 'angieslist-app-platform';
import { compose, pure } from 'recompose';
import { CaretRightIcon } from '@angi/icons';
import { getCookieByName, setExpirationDate } from './ServiceProviderDashboard';
import { defaultProps } from '../../../../app/browser/analyticsDefaultProperties';
import reviews from './assets/Reviews.svg';
import leads from './assets/Leads.svg';
import reviewsMobile from './assets/mobile/ReviewsMobile.svg';
import leadsMobile from './assets/mobile/LeadsMobile.svg';
import hours from './assets/Hours.svg';
import hoursMobile from './assets/mobile/HoursMobile.svg';
import address from './assets/Address.svg';
import addressMobile from './assets/mobile/AddressMobile.svg';
import services from './assets/Services.svg';
import servicesMobile from './assets/mobile/ServicesMobile.svg';
import SendEnhancedTrackEventOnce from '../../../../app/browser/SendEnhancedTrackEventOnce';
import './viewsCard.scss';

const leadsCookieName = 'leadsVisited';
const screenSm = 1024;
const maxCardNumber = 2;

type Props = {
  reviewCount: number,
  serviceProviderId: string,
  currentProfileUAPI: Object,
  sendEnhancedTrackEvent:
   typeof analyticsActions.sendEnhancedTrackEvent,
}

export const ViewsCard = (props: Props) : Element<"div"> => {
  const {
    reviewCount,
    serviceProviderId,
    currentProfileUAPI,
    sendEnhancedTrackEvent,
  } = props;

  const currentWindowWidth = window.innerWidth;
  const [hasCards, setHasCards] = useState(false);
  const [cardsRendered, setCardsRendered] = useState([]);
  const profileUAPIResponse = get(currentProfileUAPI, 'response', false);
  const hoursInfo = get(profileUAPIResponse, 'businessInfo.hoursInfo.hoursInfoDetail', []);
  const addressInfo = get(profileUAPIResponse, 'contactInfo.address', {});
  const servicesOfferedInfo = get(profileUAPIResponse, 'servicesOffered', '');
  const servicesNotOfferedInfo = get(profileUAPIResponse, 'servicesNotOffered', '');

  // Validation rules to show cards
  const isReviewCardEnabled = reviewCount < 1;
  const isLeadsCardEnabled = getCookieByName(leadsCookieName) !== 'true';
  const isHoursCardEnabled = hoursInfo.length < 1;
  const isAddressCardEnabled = () => {
    let validAddress = false;
    Object.keys(addressInfo).forEach((prop) => {
      if (prop !== 'street2' && addressInfo[prop] === '') {
        validAddress = true;
      }
    });
    return validAddress;
  };
  const isServicesCardEnabled = servicesOfferedInfo === '' || servicesNotOfferedInfo === '';

  const setEventProperties = () => {
    const cardsDisplayed = [];
    // these validations are needed because page loaded
    // event is triggered before the rendered of elements
    if (isReviewCardEnabled) {
      cardsDisplayed.push('ReviewCard');
    }
    if (isLeadsCardEnabled) {
      cardsDisplayed.push('LeadsCard');
    }
    if (isHoursCardEnabled && cardsDisplayed.length < maxCardNumber) {
      cardsDisplayed.push('HoursCard');
    }
    if (isAddressCardEnabled() && cardsDisplayed.length < maxCardNumber) {
      cardsDisplayed.push('AddressCard');
    }
    if (isServicesCardEnabled && cardsDisplayed.length < maxCardNumber) {
      cardsDisplayed.push('ServicesCard');
    }

    return {
      ...defaultProps,
      activityLocation: 'SP : Home',
      impressionTracking: cardsDisplayed.join('|'),
      description: 'View cards',
    };
  };

  const cardLoaded = (cardNumber) => {
    setHasCards(true);
    if (cardsRendered.length < maxCardNumber) {
      setCardsRendered([...cardsRendered, cardNumber]);
    }
  };

  const validateCard = cardNumber =>
    (cardsRendered.length < maxCardNumber || cardsRendered.indexOf(cardNumber) > -1);

  const validateHoursCard = () =>
    (isHoursCardEnabled && validateCard(3));

  const validateAddressCard = () =>
    (isAddressCardEnabled() && validateCard(4));

  const validateServicesCard = () =>
    (isServicesCardEnabled && validateCard(5));

  const redirectToPage = (card: number) => {
    const urls = [
      urlCatalog.office.requestReviews(serviceProviderId),
      urlCatalog.office.leadBoard(serviceProviderId),
      `/app/profile/${serviceProviderId}/hours`,
      `/app/profile/${serviceProviderId}/contact-information`,
      `/app/profile/${serviceProviderId}/service-details#additional`,
    ];
    if (card === 2) {
      document.cookie = `${encodeURIComponent(leadsCookieName)}=${encodeURIComponent('true')}; expires=${setExpirationDate(365)};`;
    }
    window.location.href = urls[card - 1];
  };

  return (
    <Loader
      loading={!profileUAPIResponse}
      element={
        <div className="spinner">
          <i className="icon icon-load icon-spin icon-5x text-primary" />
        </div>
      }
    >
      <div id="views-card-container">
        <If condition={profileUAPIResponse}>
          <SendEnhancedTrackEventOnce
            analyticsEventName="Item Clicked"
            analyticsEventProperties={setEventProperties()}
            sendEnhancedTrackEvent={sendEnhancedTrackEvent}
          />
        </If>
        <If condition={hasCards}>
          <div id="views-card-title-container" className="views-title National2-Bold">
            Get started
          </div>
        </If>
        <div id="views-card-items-container">
          <If condition={isReviewCardEnabled}>
            <If condition={currentWindowWidth >= screenSm}>
              <hr className="divider" />
            </If>
            <Track>
              <div
                id="views--reviews-card-container"
                className="row-views"
                onLoad={() => cardLoaded(1)}
                onClick={() => redirectToPage(1)}
                onKeyDown={() => redirectToPage(1)}
                role="button"
                tabIndex={0}
              >
                <div className="view-item-col card-icon">
                  <img id="reviews-icon" src={currentWindowWidth > screenSm ? reviews : reviewsMobile} alt="Reviews Icon" />
                </div>
                <div className="view-item-col">
                  <div>
                    <div className="view-item-title National2-Medium">
                      Add reviews from Google
                    </div>
                    <div className="view-item-subtitle National2">
                      Or request reviews from past customers.
                    </div>
                  </div>
                </div>
                <div >
                  <CaretRightIcon className="caret-icon" />
                </div>
              </div>
            </Track>
          </If>
          <If condition={isLeadsCardEnabled}>
            <If condition={currentWindowWidth >= screenSm}>
              <hr className="divider" />
            </If>
            <Track>
              <div
                id="views--leads-card-container"
                className="row-views"
                onLoad={() => cardLoaded(2)}
                onClick={() => redirectToPage(2)}
                onKeyDown={() => redirectToPage(2)}
                role="button"
                tabIndex={0}
              >
                <div className="view-item-col card-icon">
                  <img id="leads-icon" src={currentWindowWidth > screenSm ? leads : leadsMobile} alt="Leads Icon" />
                </div>
                <div className="view-item-col">
                  <div>
                    <div className="view-item-title National2-Medium">
                        Get familiar with leads
                    </div>
                    <div className="view-item-subtitle National2">
                        See our lead management tools.
                    </div>
                  </div>
                </div>
                <div className="align-center">
                  <CaretRightIcon className="caret-icon" />
                </div>
              </div>
            </Track>
          </If>
          <If condition={validateHoursCard()}>
            <If condition={currentWindowWidth >= screenSm}>
              <hr className="divider" />
            </If>
            <Track>
              <div
                id="views--hours-card-container"
                className="row-views"
                onLoad={() => cardLoaded(3)}
                onClick={() => redirectToPage(3)}
                onKeyDown={() => redirectToPage(3)}
                role="button"
                tabIndex={0}
              >
                <div className="view-item-col card-icon">
                  <img id="hours-icon" src={currentWindowWidth > screenSm ? hours : hoursMobile} alt="Hours Icon" />
                </div>
                <div className="view-item-col">
                  <div>
                    <div className="view-item-title National2-Medium">
                      Add business hours
                    </div>
                    <div className="view-item-subtitle National2">
                      Tell customers when they can reach you.
                    </div>
                  </div>
                </div>
                <div className="align-center">
                  <CaretRightIcon className="caret-icon" />
                </div>
              </div>
            </Track>
          </If>
          <If condition={validateAddressCard()}>
            <If condition={currentWindowWidth >= screenSm}>
              <hr className="divider" />
            </If>
            <Track>
              <div
                id="views--address-card-container"
                className="row-views"
                onLoad={() => cardLoaded(4)}
                onClick={() => redirectToPage(4)}
                onKeyDown={() => redirectToPage(4)}
                role="button"
                tabIndex={0}
              >
                <div className="view-item-col card-icon">
                  <img id="address-icon" src={currentWindowWidth > screenSm ? address : addressMobile} alt="Address Icon" />
                </div>
                <div className="view-item-col">
                  <div>
                    <div className="view-item-title National2-Medium">
                      Add your address
                    </div>
                    <div className="view-item-subtitle National2">
                      Tell customers where you’re located.
                    </div>
                  </div>
                </div>
                <div className="align-center">
                  <CaretRightIcon className="caret-icon" />
                </div>
              </div>
            </Track>
          </If>
          <If condition={validateServicesCard()}>
            <If condition={currentWindowWidth >= screenSm}>
              <hr className="divider" />
            </If>
            <Track>
              <div
                id="views--services-card-container"
                className="row-views"
                onLoad={() => cardLoaded(5)}
                onClick={() => redirectToPage(5)}
                onKeyDown={() => redirectToPage(5)}
                role="button"
                tabIndex={0}
              >
                <div className="view-item-col card-icon">
                  <img id="services-icon" src={currentWindowWidth > screenSm ? services : servicesMobile} alt="Services offered Icon" />
                </div>
                <div className="view-item-col">
                  <div>
                    <div className="view-item-title National2-Medium">
                      Describe your services
                    </div>
                    <div className="view-item-subtitle National2">
                      Let customers know your specific offerings.
                    </div>
                  </div>
                </div>
                <div className="align-center">
                  <CaretRightIcon className="caret-icon" />
                </div>
              </div>
            </Track>
          </If>
        </div>
      </div>
    </Loader>
  );
};

export default (compose(pure)(ViewsCard): any);



// WEBPACK FOOTER //
// ./src/modules/home/browser/components/viewsCard.js